import React, { useState, useMemo, useEffect, useRef } from 'react';
import { downloadCSV } from '../../../../utils/excelUtils';
import { keepInView } from '../shared/dom';
import { useLang, useAutoTranslate, SOURCE_LANG } from '../../i18n';
import { AuditTable } from '../shared/AuditTable';
import { DateRange } from '../shared/DateRange';
import { Segmented, Chips } from '../shared/Toggles';
import { EmptyState, Loading } from '../shared/EmptyState';
import { Banner } from '../shared/Banner';
import { fetchItemsInRange } from '../../data/items';
import { useLists } from '../../context';
import { useListNames } from '../../hooks/useListNames';
import { verbId } from '../../logic/lists';
import { itemStatus, isHandled, isFlagged, topNeeded, filterItems } from '../../logic/items';
import { daysAgoISO, fmtDateShort, fmtTime, shiftISO } from '../../logic/dates';
import { itemsToCsvRows, historyByDay, historyTotals } from '../../logic/history';
import { isOpenItem, listDayOf, listDayISO } from '../../logic/openList';
import { STATUS, STATUS_META } from '../../config/lists';
import { HISTORY_WINDOW_DAYS, HISTORY_RANGE_OPTIONS, DEFAULT_HISTORY_RANGE, TOP_NEEDED_COUNT, CSV_HEADERS } from '../../config/constants';

// ── History tab — every CLOSED list, day by day (or item by item) ─────────────
//
// An item lands here once its list is moved to History from the Today tab (older,
// pre-list-cycle items count as closed after a day). Tracking is by the day a list was
// FOR: how much got done, what didn't, what went to Review, and who closed it.

const STATUS_FILTERS = ['all', 'done', 'notDone', 'flagged'];
const NO_NAMES = [];
const matchStatus = (it, f) => (f === 'done' ? isHandled(it) : f === 'flagged' ? isFlagged(it)
  : f === 'notDone' ? itemStatus(it) === STATUS.NEEDED : true);

export function HistoryTab({ items, loading, truncated = false }) {
  const { t, ln, lang } = useLang();
  const { all: allLists, catById } = useLists();
  const { short } = useListNames();
  // Lists can be FOR tomorrow (started at closing tonight) — the newest day History shows.
  const lastDay = listDayISO();
  const [days, setDays] = useState(DEFAULT_HISTORY_RANGE);
  const [from, setFrom] = useState('');     // custom bounds override the preset
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [view, setView] = useState('day');  // day | items

  // Presets include today ("7 days" = today + the 6 before). An open custom start
  // reaches back as far as the longest preset.
  const custom = !!(from || to);
  const maxPreset = HISTORY_RANGE_OPTIONS[HISTORY_RANGE_OPTIONS.length - 1];
  const range = custom
    ? { from: from || daysAgoISO(maxPreset - 1), to: to || lastDay }
    : { from: daysAgoISO(days - 1), to: lastDay };
  const pickPreset = d => { setDays(d); setFrom(''); setTo(''); };

  // Older than what's loaded live → one-shot fetch (a list-day can be its noted date + 1,
  // hence the extra day at the start). The live feed covers HISTORY_WINDOW_DAYS — unless
  // it hit its record cap, in which case it only reaches its oldest loaded day (maybe
  // partially), so fetch up to and including that day; duplicates are dropped by id.
  const oldestLive = useMemo(() => items.reduce((m, i) => (i.date && (!m || i.date < m) ? i.date : m), ''), [items]);
  const olderTo = truncated && oldestLive ? oldestLive : shiftISO(daysAgoISO(HISTORY_WINDOW_DAYS), -1);
  const needOlder = range.from <= olderTo;
  const olderKey = `${range.from}|${olderTo}`;
  const [older, setOlder] = useState({ key: '', items: [], loading: false, failed: false });
  const [olderTry, setOlderTry] = useState(0);   // bumped by "Try again"
  const olderCache = useRef(new Map());   // "from|to" → fetched items (this visit)
  useEffect(() => {
    if (!needOlder) return undefined;
    const cached = olderCache.current.get(olderKey);
    if (cached) { setOlder({ key: olderKey, items: cached, loading: false, failed: false }); return undefined; }
    let alive = true;
    setOlder({ key: olderKey, items: [], loading: true, failed: false });
    const [f, to2] = olderKey.split('|');
    fetchItemsInRange(shiftISO(f, -1), to2)
      .then(list => { olderCache.current.set(olderKey, list); if (alive) setOlder({ key: olderKey, items: list, loading: false, failed: false }); })
      // Offline / over quota: say so (an empty range would look like "no history").
      .catch(() => { if (alive) setOlder({ key: olderKey, items: [], loading: false, failed: true }); });
    return () => { alive = false; };
  }, [needOlder, olderKey, olderTry]);

  // Closed items inside the range (by the day the list was for), then search + list.
  const scoped = useMemo(() => {
    const pool = needOlder ? [...items, ...older.items] : items;
    const seen = new Set();
    const inRange = pool.filter(i => {
      if (seen.has(i.id)) return false;
      seen.add(i.id);
      const d = listDayOf(i);
      return !isOpenItem(i) && d >= range.from && d <= range.to;
    });
    return filterItems(inRange, q, catFilter);
  }, [items, older.items, needOlder, range.from, range.to, q, catFilter]);
  const filtered = useMemo(() => scoped.filter(i => matchStatus(i, statusFilter)), [scoped, statusFilter]);
  useAutoTranslate(useMemo(() => (lang === SOURCE_LANG ? NO_NAMES : filtered.map(i => i.name)), [lang, filtered]));

  // List filter: every list on the board, plus deleted ones that still have history here.
  const filterLists = useMemo(() => {
    const used = new Set(items.map(i => i.category));
    return allLists.filter(l => !l.deleted || used.has(l.id));
  }, [allLists, items]);

  const totals = useMemo(() => historyTotals(scoped), [scoped]);   // KPIs ignore the status chip
  const top = useMemo(() => topNeeded(scoped, TOP_NEEDED_COUNT), [scoped]);
  const dayRows = useMemo(() => historyByDay(filtered), [filtered]);

  // Per-list done word: buy-lists are "Refilled", task lists "Prepped/Cooked/Cleaned…".
  const statusCell = r => {
    const s = itemStatus(r);
    const m = STATUS_META[s];
    const label = s === STATUS.NEEDED ? t('history.notDone')
      : s === STATUS.REFILLED && !catById(r.category).sourceable ? t(`act.done.${verbId(r.category)}`) : t(`status.${s}`);
    return <span className="do-htable__badge" style={{ '--sc': s === STATUS.NEEDED ? 'var(--muted)' : m.color }}>{m.icon} {label}</span>;
  };
  const who = (name, at) => (name ? `${name}${at ? ` · ${fmtTime(at)}` : ''}` : '—');

  const itemCols = useMemo(() => [
    { key: 'day', label: t('col.listFor'), sortVal: r => listDayOf(r), cell: r => fmtDateShort(listDayOf(r)), className: 'do-htable__date' },
    { key: 'category', label: t('col.list'), sortVal: r => short(r.category),
      cell: r => `${catById(r.category).icon} ${short(r.category)}` },
    { key: 'name', label: t('col.item'), cell: r => ln(r.name), className: 'do-htable__item' },
    { key: 'qty', label: t('col.qty'), cell: r => r.qty || '—', className: 'do-htable__date' },
    { key: 'status', label: t('col.status'), sortVal: r => itemStatus(r), cell: statusCell },
    { key: 'addedBy', label: t('col.addedBy'), cell: r => r.addedBy || '—', className: 'do-htable__by' },
    { key: 'handledBy', label: t('col.handledBy'), sortVal: r => r.doneBy || r.flaggedBy || '',
      cell: r => (r.doneBy ? who(r.doneBy, r.doneAt) : who(r.flaggedBy, r.flaggedAt)), className: 'do-htable__by' },
    { key: 'archivedOn', label: t('col.closed'), sortVal: r => r.archivedOn || '',
      cell: r => (r.archivedOn ? `${fmtDateShort(r.archivedOn)}${r.archivedBy ? ` · ${r.archivedBy}` : ''}` : '—'), className: 'do-htable__by' },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, ln, short, catById]);

  const dayCols = useMemo(() => [
    { key: 'day', label: t('col.listFor'), cell: r => fmtDateShort(r.day), className: 'do-htable__date' },
    { key: 'total', label: t('col.items'), sortVal: r => r.total, cell: r => <b>{r.total}</b> },
    { key: 'done', label: t('col.done'), sortVal: r => r.done, cell: r => <span className="do-hday__n do-hday__n--done">{r.done}</span> },
    { key: 'notDone', label: t('col.notDone'), sortVal: r => r.notDone, cell: r => <span className="do-hday__n do-hday__n--todo">{r.notDone}</span> },
    { key: 'flagged', label: t('col.flagged'), sortVal: r => r.flagged, cell: r => (r.flagged ? <span className="do-hday__n do-hday__n--flag">{r.flagged}</span> : '—') },
    { key: 'pct', label: t('col.completion'), sortVal: r => r.pct,
      cell: r => (
        <span className="do-hday__pct" title={`${r.pct}%`}>
          <span className="do-hday__bar"><i style={{ width: `${r.pct}%` }} /></span>{r.pct}%
        </span>
      ) },
    { key: 'closedOn', label: t('col.closed'), sortVal: r => r.closedOn,
      cell: r => (r.closedOn ? `${fmtDateShort(r.closedOn)}${r.closedBy ? ` · ${r.closedBy}` : ''}` : '—'), className: 'do-htable__by' },
  ], [t]);

  // A day's items, grouped by list (in board order; lists since deleted included), with
  // status + qty + who noted it.
  const dayDetail = r => (
    <div className="do-hday__detail">
      {[...new Set(r.items.map(i => i.category))].map(catById).sort((a, b) => a.order - b.order).map(c => {
        const list = r.items.filter(i => i.category === c.id);
        return (
          <div key={c.id} className="do-hday__cat" style={{ '--cc': c.color }}>
            <span className="do-hday__cat-name">{c.icon} {short(c)} · {list.length}</span>
            <div className="do-hday__items">
              {list.map(i => {
                const s = itemStatus(i);
                return (
                  <span key={i.id} className={`do-hday__item do-hday__item--${s}`} title={i.addedBy ? t('history.notedBy', { name: i.addedBy }) : undefined}>
                    {s === STATUS.NEEDED ? '○' : STATUS_META[s].icon} {ln(i.name)}{i.qty ? <b> {i.qty}</b> : null}
                  </span>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );

  const exportCsv = () => {
    if (filtered.length) downloadCSV(CSV_HEADERS, itemsToCsvRows(filtered, short), `daily-ops-history-${range.from}-to-${range.to}`);
  };
  const rangeLabel = custom
    ? `${fmtDateShort(range.from)} → ${fmtDateShort(range.to)}`
    : t('history.lastDays', { n: days });
  const busy = loading || (needOlder && older.loading);

  return (
    <div className="do-history">
      <div className="do-history__controls">
        <DateRange presets={HISTORY_RANGE_OPTIONS} presetLabel={d => t('history.days', { n: d })}
          days={days} onPreset={pickPreset} from={from} to={to} onFrom={setFrom} onTo={setTo}
          onClear={() => { setFrom(''); setTo(''); }} max={lastDay}
          fromAria={t('history.fromAria')} toAria={t('history.toAria')} clearLabel={t('history.clearDates')} />
        <input className="do-history__search" type="search" placeholder={t('history.searchItems')} onFocus={keepInView}
          value={q} onChange={e => setQ(e.target.value)} aria-label={t('history.searchAria')} />
        <select className="do-history__filter" value={catFilter} onChange={e => setCatFilter(e.target.value)} aria-label={t('history.filterAria')}>
          <option value="all">{t('history.allLists')}</option>
          {filterLists.map(c => <option key={c.id} value={c.id}>{c.icon} {short(c)}</option>)}
        </select>
        <button className="do-history__export" onClick={exportCsv} disabled={!filtered.length} title={t('history.csvTitle')}>{t('common.csv')}</button>
      </div>

      <div className="do-history__bar">
        <Chips label={t('history.statusAria')} value={statusFilter} onChange={setStatusFilter}
          options={STATUS_FILTERS.map(f => [f, t(`history.status.${f}`)])} />
        <span className="do-checklist__spacer" />
        <Segmented className="do-hist-views" value={view} onChange={setView}
          options={[['day', t('history.viewByDay')], ['items', t('history.viewItems', { n: filtered.length })]]} />
      </div>

      {!busy && scoped.length > 0 && (
        <div className="do-kpis">
          <div className="do-kpi"><span className="do-kpi__val">{totals.days}</span><span className="do-kpi__lbl">{t('history.kpiLists')}</span></div>
          <div className="do-kpi"><span className="do-kpi__val">{totals.items}</span><span className="do-kpi__lbl">{t('history.itemsNoted')}</span></div>
          <div className="do-kpi"><span className="do-kpi__val do-kpi__val--done">{totals.pct}%</span><span className="do-kpi__lbl">{t('history.kpiDone', { n: totals.done })}</span></div>
          <div className="do-kpi"><span className="do-kpi__val">{totals.notDone}</span><span className="do-kpi__lbl">{t('history.kpiNotDone')}</span></div>
          <div className="do-kpi do-kpi--wide">
            <span className="do-kpi__lbl">{t('history.mostNeeded')}</span>
            <div className="do-kpi__top">
              {top.map(p => (
                <span key={p.category + p.name} className="do-kpi__pill" style={{ '--cc': catById(p.category).color }}>
                  {catById(p.category).icon} {ln(p.name)} <b>×{p.count}</b>
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
      {!busy && scoped.length > 0 && <div className="do-audit__range-note">{t('history.showing', { range: rangeLabel })}</div>}
      {needOlder && older.failed && (
        <Banner icon="⚠️" action={<button className="do-reminder__send" onClick={() => setOlderTry(n => n + 1)}>{t('common.retry')}</button>}>
          {t('history.olderFailed', { date: fmtDateShort(shiftISO(olderTo, 1)) })}
        </Banner>
      )}

      {busy ? (
        <Loading label={needOlder && older.loading ? t('history.loadingOlder') : t('history.loading')} />
      ) : filtered.length === 0 ? (
        <EmptyState icon={q || catFilter !== 'all' || statusFilter !== 'all' || custom ? '🔍' : '🗓️'}
          title={scoped.length || q || catFilter !== 'all' ? t('history.noMatching') : t('history.noHistoryRange')}
          sub={scoped.length || q || catFilter !== 'all' ? t('history.tryDifferent') : t('history.appearTomorrow')} />
      ) : view === 'day' ? (
        <AuditTable columns={dayCols} rows={dayRows} defaultSort={{ key: 'day', dir: 'desc' }} rowKey={r => r.key}
          rowClass={r => (r.notDone > 0 ? 'do-hday__row--open' : undefined)} renderDetail={dayDetail} detailLabel={t('history.showItems')} />
      ) : (
        <AuditTable columns={itemCols} rows={filtered} defaultSort={{ key: 'day', dir: 'desc' }} rowKey={r => r.id} />
      )}
    </div>
  );
}
